import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEarlyWarningsFilters } from '../../../hooks/useEarlyWarningsFilters';
import { FilterConfigValues, IconCode, TabFilterOption } from '../../../models/EarlyWarnings';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { PENDING_SIGNATURE_OR_LEGIS_APPROVAL } from '../../../services/sessionFiltersIndicatorDetailHelper';
import { EarlyWarningsFilters as PendingSignatureEarlyWarningsFilters } from '../EarlyWarningsFilters/EarlyWarningsFilters';
import { createFilterConfigComponent } from '../EarlyWarningsFilters/Util';
import styles from './PendigSignatureOrLegisApproval.module.scss';
import { PendingSignatureTable } from './Table/Table';
import { TabsFilter as PendigSignatureTabFilter } from './TabsFilter/TabsFilter';
import { showEarlyWarningModule } from './Util';
import { handleLoading } from '../../utils';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { useTranslation } from 'react-i18next';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';

const filterConfigValues: FilterConfigValues[] = [
  { id: 0, filterIcon: IconCode.GREEN, filterLabel: 'within_the_regulation_time' },
  { id: 1, filterIcon: IconCode.YELLOW, filterLabel: 'up_to_one_year_extension' },
  { id: 2, filterIcon: IconCode.RED, filterLabel: 'more_than_one_year_extension' },
];
const filterConfigComponent: TabFilterOption[] = createFilterConfigComponent(filterConfigValues);

const defaultSessionFilterObject = {
  selectedTab: 0,
  filterConfigState: filterConfigComponent,
  changeProjectType: true,
};

export const PendigSignatureOrLegisApproval = () => {
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false)
  const { currentIndicator } = useSelector<any, any>(state => state);
  const { projectTypeFilterOnDetail } = useSelector<any, any>(state => state.indicatorDetailFilter);
  const { filterConfigState, handleEarlyWarningsFilter, handleProjectsQuantities, selectedTab, setSelectedTab } =
    useEarlyWarningsFilters({
      sessionKey: PENDING_SIGNATURE_OR_LEGIS_APPROVAL,
      defaultSessionFilterObject,
      projectTypeFilterOnDetail,
    });
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { container, tabsContainer, filtersEarlyWarningsContainer, tableContainer, centerErrorMessage } = styles;

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = useState(false);
  const [totalProjectsData, setTotalProjectsData] = useState(false);
  const [flag, setFlag] = useState<boolean>(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };
  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);
    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
      setHasError(true);
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(
      setCurrentIndicator(
        sendEndSummary?.find(data => data.indicatorKey === 'PROJECTS_PENDING_SIGNATURE_OR_LEGISLATIVE_APPROVAL'),
      ),
    );
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (projectTypeFilterOnDetail === 0) {
      getFilteredSimpleIndicators(
        dispatch,
        currentIndicator.id,
        currentIndicator.indicatorKey,
        setLoadingData,
        // projectTypeFilterChange,
      );
    } else {
      getFilteredSimpleIndicators(dispatch, currentIndicator.id, currentIndicator.indicatorKey, setLoadingData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, projectTypeFilterOnDetail, filterIndicators.common]);

  useEffect(() => {
    if (!sessionStorage.getItem(PENDING_SIGNATURE_OR_LEGIS_APPROVAL)) {
      sessionStorage.setItem(PENDING_SIGNATURE_OR_LEGIS_APPROVAL, JSON.stringify(defaultSessionFilterObject));
    }
  }, []);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  const handleFilterApplied = filter => {
    setSelectedTab(filter);
  };

  return (
    <>
      <div className={container}>
        <div className={tabsContainer}>
          <PendigSignatureTabFilter filterBy={selectedTab} handleFilterApplied={handleFilterApplied} />
        </div>
        {!hasError ? (
          <>
            {showEarlyWarningModule(projectTypeFilterOnDetail, selectedTab) && (
              <div className={filtersEarlyWarningsContainer}>
                <PendingSignatureEarlyWarningsFilters
                  filterSet={filterConfigState}
                  handleFilter={handleEarlyWarningsFilter}
                />
              </div>
            )}
            <div className={tableContainer}>
              <PendingSignatureTable
                selectedTab={selectedTab}
                earlyWarningsTab={[
                  filterConfigState[0].selected,
                  filterConfigState[1].selected,
                  filterConfigState[2].selected,
                ]}
                projectsQuantities={handleProjectsQuantities}
                loading={loadingData}
              />
            </div>
          </>
        ) : (
          <div className={styles.centerErrorMessage}>
            <ErrorMessageView
              title={t('title_error_message_view')}
              description={t('desc_error_message_tab')}
              reload={handleCommonFilterChange}
              isIndicator={true}
            />
          </div>
        )}
      </div>
    </>
  );
};

const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  projectTypeFilterChange?,
) => {
  return handleLoading(
    () => d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, projectTypeFilterChange)),
    setLoading,
  );
};
